import React, { useState, useEffect } from 'react';
import { object } from 'prop-types';
import { get } from 'lodash';
import axios from 'axios';
import { loadReCaptcha, ReCaptcha } from 'react-recaptcha-v3';
import { publicRuntimeConfig } from '../../../config';
import { listProducts } from '../../../services/marketplace';
import {
    CustomLogo,
    FlexContainer,
    IsMobileWrapper,
    Main,
    RootContainer,
    Row,
    Text,
    Notification,
    If
} from '../../common';
import PinSection from './components/PinSection';
import OrderReception from './components/OrderReception';
import OrderReceptionModal from './components/OrderReceptionModal';

// Configuración para el servicio de command-products
const { recaptchaToken, commandOrdersUrl, commandOrdersApiKey } = publicRuntimeConfig.settings;

const Marketplace = () => {
    // Obtener storeId y sectorCommand desde la URL
    // Ejemplo: /marketplace/J780M/800
    const getParamsFromUrl = () => {
        const pathname = window.location.pathname;
        const parts = pathname.split('/').filter(part => part !== '');
        // Asumiendo que la estructura es /marketplace/:storeId/:sectorCommand
        const storeId = parts[1] || null;
        const sectorCommand = parts[2] || null;
        return { storeId, sectorCommand };
    };

    const { storeId, sectorCommand } = getParamsFromUrl();
    
    const [pin, setPin] = useState('');
    const [orderData, setOrderData] = useState(null);
    const [error, setError] = useState(null);
    const [modalError, setModalError] = useState(null);
    const [receivedQuantities, setReceivedQuantities] = useState({});
    const [deliveredBy, setDeliveredBy] = useState('');
    const [receivedBy, setReceivedBy] = useState('');
    const [showSuccess, setShowSuccess] = useState(false);
    const [loading, setLoading] = useState(false);
    const [productsData, setProductsData] = useState(null);
    const [loadingProducts, setLoadingProducts] = useState(false);
    const [mktStoreName, setMktStoreName] = useState('');
    const [modalVisible, setModalVisible] = useState(false);
    const [modalMode, setModalMode] = useState('fromPin'); // 'fromPin' | 'fromEye'
    const [selectedOrderFromTable, setSelectedOrderFromTable] = useState(null);

    useEffect(() => {
        loadReCaptcha(recaptchaToken);
        // Esperar a que reCAPTCHA se cargue y verificar que tengamos los parámetros
        if (storeId && sectorCommand) {
            setTimeout(() => {
                fetchProducts();
            }, 1000);
        }

        // Configurar refresco automático cada 1 minuto (60000 ms)
        const intervalId = setInterval(() => {
            if (storeId && sectorCommand && window.grecaptcha) {
                fetchProducts();
            }
        }, 60000);

        // Limpiar el intervalo cuando el componente se desmonte
        return () => {
            clearInterval(intervalId);
        };
    }, []);

    const fetchProducts = async () => {
        // Verificar que reCAPTCHA esté disponible
        if (!window.grecaptcha) {
            console.error('reCAPTCHA no está disponible aún');
            return;
        }

        // Verificar que tengamos los parámetros necesarios
        if (!storeId || !sectorCommand) {
            console.error('Faltan parámetros: storeId o sectorCommand');
            return;
        }

        setLoadingProducts(true);
        try {
            const response = await listProducts({
                storeId: storeId,
                sectorCommand: parseInt(sectorCommand),
                statusId: [2],
                statusPin: 1,
                isDeleted: false
            });

            if (response.internalCode === 200 && response.payload) {
                // Mapear órdenes con información agregada
                const ordersData = response.payload.map(order => ({
                    orderId: order.orderId,
                    itemCount: order.products.length,
                    deliveryDate: order.shippingEta?.init || null,
                    products: order.products
                }));

                setProductsData(ordersData);

                // Extraer mktStoreName del primer producto de la primera orden
                if (response.payload.length > 0 && response.payload[0].products.length > 0) {
                    setMktStoreName(response.payload[0].products[0].mktStore || '');
                }
            }
        } catch (error) {
            console.error('Error fetching products:', error);
        } finally {
            setLoadingProducts(false);
        }
    };

    const validatePin = async (skipViewChange = false, setErrorCallback = setError) => {
        setErrorCallback(null);
        setShowSuccess(false);
        setLoading(true);

        try {
            return await new Promise((resolve) => {
                window.grecaptcha.ready(async () => {
                    try {
                        const token = await window.grecaptcha.execute(recaptchaToken, { action: 'submit' });

                        const { data } = await axios.request({
                            method: 'post',
                            url: `${commandOrdersUrl}/command/validatePin`,
                            headers: {
                                'Content-Type': 'application/json',
                                'x-api-key': commandOrdersApiKey,
                                'x-recaptcha-token': token,
                                'isfront': true
                            },
                            data: { pin }
                        });

                        if (data.internalCode === 200 && data.payload) {
                            const responseData = data.payload;

                            // Validar que tenga productos
                            if (!responseData.products || responseData.products.length === 0) {
                                setErrorCallback('Por favor, verifica el PIN e intenta nuevamente.');
                                if (!skipViewChange) {
                                    setOrderData(null);
                                }
                                setLoading(false);
                                resolve(false);
                                return;
                            }

                            // Mapear productos con IDs únicos para el manejo de cantidades
                            const productsWithIds = responseData.products.map((product, index) => ({
                                ...product,
                                id: `${responseData.orderId}-${index}` // ID único basado en orden e índice
                            }));

                            // Crear objeto de orden con estructura esperada por el componente
                            const order = {
                                orderId: responseData.orderId,
                                seller: responseData.mktStore,
                                products: productsWithIds
                            };

                            // Inicializar cantidades disponibles en 0 (el seller las ingresa)
                            const initialQuantities = {};
                            productsWithIds.forEach(product => {
                                initialQuantities[product.id] = 0;
                            });

                            if (!skipViewChange) {
                                setOrderData(order);
                                setReceivedQuantities(initialQuantities);
                                setDeliveredBy('');
                                setReceivedBy('');
                            }
                            
                            setLoading(false);
                            resolve(true);
                        } else {
                            setErrorCallback('Por favor, verifica el PIN e intenta nuevamente.');
                            if (!skipViewChange) {
                                setOrderData(null);
                            }
                            setLoading(false);
                            resolve(false);
                        }
                    } catch (error) {
                        console.error('Error validating pin:', error);
                        setErrorCallback('Por favor, verifica el PIN e intenta nuevamente.');
                        if (!skipViewChange) {
                            setOrderData(null);
                        }
                        setLoading(false);
                        resolve(false);
                    }
                });
            });
        } catch (error) {
            console.error('Error validating pin:', error);
            setErrorCallback('Por favor, verifica el PIN e intenta nuevamente.');
            if (!skipViewChange) {
                setOrderData(null);
            }
            setLoading(false);
            return false;
        }
    };

    const handleQuantityChange = (productId, value, maxQuantity) => {
        const numValue = parseInt(value) || 0;
        // No permitir cantidades mayores a las solicitadas
        const finalValue = numValue > maxQuantity ? maxQuantity : numValue;
        setReceivedQuantities({
            ...receivedQuantities,
            [productId]: finalValue
        });
    };

    const handleValidateAndOpenModal = async () => {
        const isValid = await validatePin(false);
        if (isValid) {
            openModalFromPin();
        }
    };

    const openModalFromPin = () => {
        setModalVisible(true);
        setModalMode('fromPin');
        setModalError(null);
    };

    const openModalFromEye = (order) => {
        setModalVisible(true);
        setModalMode('fromEye');
        setSelectedOrderFromTable(order);
        setPin('');
        
        // Mapear productos de la orden seleccionada con IDs únicos
        const productsWithIds = order.products.map((product, index) => ({
            ...product,
            id: product.id || `${order.orderId}-${index}`,
            name: product.nameComplete || product.name,
            requestedQuantity: product.quantityOriginal || product.requestedQuantity
        }));

        // Crear objeto de orden con estructura esperada
        const formattedOrder = {
            orderId: order.orderId,
            seller: order.products[0]?.mktStore || '',
            products: productsWithIds
        };

        // Inicializar cantidades en 0
        const initialQuantities = {};
        productsWithIds.forEach(product => {
            initialQuantities[product.id] = 0;
        });

        setOrderData(formattedOrder);
        setReceivedQuantities(initialQuantities);
        setModalError(null);
        setShowSuccess(false);
    };

    const closeModal = () => {
        setModalVisible(false);
        setSelectedOrderFromTable(null);
        setModalError(null);
        setShowSuccess(false);
        // Si el modal se abrió desde el PIN, limpiar también orderData
        if (modalMode === 'fromPin') {
            setOrderData(null);
            setReceivedQuantities({});
        }
    };

    const handleModalSubmit = async () => {
        // Validaciones
        if (!pin) {
            setModalError('Por favor, ingresa el PIN para confirmar.');
            return;
        }

        // Si viene del ícono del ojo, primero validar el PIN
        if (modalMode === 'fromEye') {
            const isValid = await validatePin(true, setModalError);
            if (!isValid) {
                return; // El error ya fue seteado en validatePin
            }
        }

        // Llamar al servicio de confirmación
        await handleSubmit();
    };

    const getErrorMessageByCode = (internalCode, defaultMessage) => {
        // Mapeo de códigos internos a mensajes controlados por el frontend
        const errorMessages = {
            404: 'El PIN no fue encontrado o ya ha sido utilizado anteriormente.',
            400: 'Los datos enviados no son válidos. Por favor, verifica la información.',
            401: 'No tienes autorización para realizar esta acción.',
            403: 'Acceso denegado. Contacta al administrador.',
            500: 'Ocurrió un error en el servidor. Intenta nuevamente en unos momentos.',
            503: 'El servicio no está disponible temporalmente. Intenta más tarde.'
        };

        return errorMessages[internalCode] || defaultMessage || 'No se pudo completar la operación. Por favor, intenta nuevamente.';
    };

    const getErrorMessage = (error) => {
        // Convertir errores técnicos a mensajes amigables
        const errorString = error?.message || error || '';

        if (errorString.includes('404') || errorString.includes('not found')) {
            return 'No se encontró la información solicitada. Verifica que los datos sean correctos.';
        }
        if (errorString.includes('401') || errorString.includes('403') || errorString.includes('unauthorized')) {
            return 'No tienes autorización para realizar esta acción. Contacta al administrador.';
        }
        if (errorString.includes('500') || errorString.includes('Internal Server Error')) {
            return 'Estamos teniendo problemas temporales. Intenta nuevamente en unos momentos.';
        }
        if (errorString.includes('timeout') || errorString.includes('timed out')) {
            return 'La operación tardó demasiado tiempo. Verifica tu conexión e intenta nuevamente.';
        }
        if (errorString.includes('network') || errorString.includes('Network')) {
            return 'Hay un problema de conexión. Verifica tu red e intenta nuevamente.';
        }

        return errorString || 'No se pudo completar la operación. Por favor, intenta nuevamente.';
    };

    const handleSubmit = async () => {
        // Validaciones
        if (!pin) {
            setModalError('Por favor, ingresa el PIN para confirmar.');
            return;
        }

        setLoading(true);
        setModalError(null);

        try {
            await window.grecaptcha.ready(async () => {
                const token = await window.grecaptcha.execute(recaptchaToken, { action: 'submit' });

                // Mapear productos con sus cantidades disponibles
                const products = orderData.products.map(product => ({
                    ean: product.ean,
                    quantity: receivedQuantities[product.id] || 0
                }));

                const { data } = await axios.request({
                    method: 'post',
                    url: `${commandOrdersUrl}/command/confirmPicking`,
                    headers: {
                        'Content-Type': 'application/json',
                        'x-api-key': commandOrdersApiKey,
                        'x-recaptcha-token': token,
                        'isFront': 'true'
                    },
                    data: {
                        pin,
                        deliveredBy: '-', // Valores por defecto ya que se eliminaron del formulario
                        receivedBy: '-',
                        products
                    }
                });

                if (data.internalCode === 200) {
                    setShowSuccess(true);
                    setModalError(null);
                    // Refrescar la lista de pedidos pendientes
                    await fetchProducts();
                    // No redirigir automáticamente, mantener el formulario visible
                } else {
                    // Usar mapeo de códigos de error controlado por el frontend
                    setModalError(getErrorMessageByCode(data.internalCode, data.message));
                }

                setLoading(false);
            });
        } catch (error) {
            console.error('Error confirming picking: ', error);

            // Intentar extraer mensaje del error de axios
            if (error.response && error.response.data) {
                const errorData = error.response.data;
                if (errorData.internalCode) {
                    setModalError(getErrorMessageByCode(errorData.internalCode, errorData.message));
                } else {
                    setModalError(getErrorMessage(error));
                }
            } else {
                setModalError(getErrorMessage(error));
            }

            setLoading(false);
        }
    };

    const resetForm = () => {
        setPin('');
        setOrderData(null);
        setReceivedQuantities({});
        setDeliveredBy('');
        setReceivedBy('');
        setError(null);
        setModalError(null);
        setShowSuccess(false);
    };

    const isSubmitDisabled = () => {
        return !pin || !orderData || loading;
    };

    return (
        <RootContainer>
            <Main background='F4F4F4'>
                <IsMobileWrapper>
                    {({ isMobile }) => {
                        return (
                            <>
                                {/* Header Principal - Fuera de la tarjeta */}
                                <FlexContainer padding='24px 0 0 0' direction='column'>
                                    <Row justify='center' padding={!isMobile ? 25 : 0}>
                                        <FlexContainer width='100%' justify='center'>
                                            <CustomLogo style={{ width: isMobile ? window.innerWidth * 0.5 : '195px', marginBottom: '18px' }} />
                                        </FlexContainer>
                                        <Text text='Preparación de Productos Marketplace' strong size={24} />
                                    </Row>
                                </FlexContainer>

                                {/* Vista inicial: Input PIN y Tabla de pedidos pendientes */}
                                <PinSection
                                    storeName={mktStoreName}
                                    pin={pin}
                                    onChange={setPin}
                                    onValidate={handleValidateAndOpenModal}
                                    loading={loading}
                                    error={error}
                                    onErrorClose={() => setError(null)}
                                    pendingOrders={productsData}
                                    loadingProducts={loadingProducts}
                                    onOrderEyeClick={openModalFromEye}
                                />

                                <ReCaptcha
                                    sitekey={recaptchaToken}
                                    action='submit'
                                />

                                {/* Success Notification */}
                                <If
                                    condition={showSuccess && !modalVisible}
                                    then={
                                        <FlexContainer padding={18} justify='center'>
                                            <Notification
                                                type='success'
                                                message='✓ Entrega confirmada exitosamente. Los productos han sido registrados correctamente.'
                                            />
                                        </FlexContainer>
                                    }
                                />

                                {/* Modal de recepción */}
                                <OrderReceptionModal
                                    visible={modalVisible}
                                    onClose={closeModal}
                                    order={orderData}
                                    pin={pin}
                                    onPinChange={setPin}
                                    isPinReadOnly={modalMode === 'fromPin'}
                                    quantities={receivedQuantities}
                                    onQuantityChange={handleQuantityChange}
                                    onSubmit={handleModalSubmit}
                                    loading={loading}
                                    error={modalError}
                                    showSuccess={showSuccess}
                                    onErrorClose={() => setModalError(null)}
                                    onSuccessClose={() => setShowSuccess(false)}
                                />
                            </>
                        );
                    }}
                </IsMobileWrapper>
            </Main>
        </RootContainer>
    );
};

export default Marketplace;
